import { useState, useMemo, useEffect, memo } from 'react';
import { ProductPipeline } from './ProductPipeline';
import { ProductListingsContainer } from './shared/ProductListingsContainer';
import { CompaniesTable } from './CompaniesTable';
import { ProductPortfolioSection } from './dashboard/sections/ProductPortfolioSection';
import { ProductCosts } from './costs/ProductCosts';
import { ComponentsList } from './ComponentsList';
import { getCompanies } from '../../utils/companyDataLoader';
import { useAllProducts } from '../../stores/marketStore';
import { useInventoryStore } from '../../stores/inventoryStore';
import { TabContainer, Tab } from '../ui';
import type { Product } from '@smartphone-tycoon/shared-core';
import type { Company } from '@smartphone-tycoon/shared-core';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { MarketInsights } from './MarketInsights';
import { useChartColors } from '../../hooks/useChartColors';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement);

interface ProductsViewProps {
  products: Product[];
  onProductAction: (productId: string, action: string) => void;
  gameDate: {
    day: number;
    month: number;
    year: number;
  };
  companyState: {
    cash: number;
    year: number;
    month: number;
    marketShare: number;
    reputation: number;
    dailyStats?: {
      revenue: number;
      costs: number;
      profit: number;
      productSales?: Array<{
        productId: string;
        unitsSold: number;
        revenue: number;
      }>;
    };
  };
  companyName: string;
  ceoName?: string;
  getPhaseProgress?: (product: Product) => { progress: number; daysRemaining: number; totalDays: number };
  releaseModalOpen?: boolean;
  productToRelease?: Product;
  productReviews?: {
    professional: {
      score: number;
      pros: string[];
      cons: string[];
    };
    customer: {
      averageRating: number;
      reviews: Array<{
        rating: number;
        comment: string;
      }>;
    };
  };
  onReleaseModalChange?: (open: boolean, product: Product | undefined, reviews: ProductsViewProps['productReviews']) => void;
  discontinueModalOpen?: boolean;
  productToDiscontinue?: Product;
  onDiscontinueModalChange?: (open: boolean, product: Product | null) => void;
  productAllocations?: Array<{
    productId: string;
    productName: string;
    allocations: Array<{
      phase: string;
      employeeType: string;
      count: number;
    }>;
    totalEmployees: number;
  }>;
  isProductStagePaused?: (productId: string) => boolean;
  onCashUpdate?: (newCash: number) => void;
  onEvent?: (event: {
    type: string;
    message: string;
    order?: {
      id: string;
      componentId: string;
      quantity: number;
      unitCost: number;
      totalCost: number;
      orderDate: { day: number; month: number; year: number };
      expectedDelivery: { day: number; month: number; year: number };
      status: 'pending' | 'delivered' | 'cancelled';
      supplierId: string;
    };
  }) => void;
}

export const ProductsView = memo(function ProductsView({
  products,
  onProductAction,
  gameDate,
  companyState,
  companyName,
  ceoName,
  getPhaseProgress,
  releaseModalOpen,
  productToRelease,
  productReviews,
  onReleaseModalChange,
  discontinueModalOpen,
  productToDiscontinue,
  onDiscontinueModalChange,
  productAllocations,
  isProductStagePaused,
  onCashUpdate,
  onEvent
}: ProductsViewProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'pipeline' | 'insights' | 'listings' | 'companies' | 'costs' | 'components'>('overview');
  const [companies, setCompanies] = useState<Company[]>([]);

  // Get all products (player + competitor) from unified source
  const { allProducts, loading: productsLoading } = useAllProducts();

  // Get inventory data
  const devices = useInventoryStore(state => state.devices);

  // Get competitor companies with accurate product counts from database
  useEffect(() => {
    const fetchCompanies = async () => {
      const companiesData = await getCompanies(
        { year: companyState.year },
        companyName && ceoName ? { name: companyName, ceoName } : undefined,
        true // Include database products for accurate counts
      );
      setCompanies(companiesData);
    };
    
    fetchCompanies();
  }, [companyState.year, companyName, ceoName]); // Only re-fetch when these core values change

  // Calculate market share data from all products (player + competitor)
  const marketShareData = useMemo(() => {
    
    const activeProducts = allProducts.filter(p => p.unitsSold > 0 && p.status !== 'discontinued');
    const totalUnits = activeProducts.reduce((sum, p) => sum + p.unitsSold, 0);
    
    // Calculate company market share
    const companyData = new Map();
    activeProducts.forEach(p => {
      const existing = companyData.get(p.companyName) || { name: p.companyName, units: 0, isPlayer: p.isPlayerProduct };
      existing.units += p.unitsSold;
      companyData.set(p.companyName, existing);
    });
    
    const companies = Array.from(companyData.values()).map(c => ({
      ...c,
      marketShare: totalUnits > 0 ? (c.units / totalUnits) * 100 : 0
    }));
    
    return {
      products: activeProducts.map(p => ({
        ...p,
        marketShare: totalUnits > 0 ? (p.unitsSold / totalUnits) * 100 : 0
      })),
      companies,
      totalUnits
    };
  }, [allProducts]);

  // Pie chart colors from theme
  const colors = useChartColors();

  // Product pie chart data
  const productPieData = useMemo(() => ({
    labels: marketShareData.products.slice(0, 10).map(p => `${p.name} (${p.companyName})`),
    datasets: [{
      data: marketShareData.products.slice(0, 10).map(p => p.marketShare),
      backgroundColor: colors,
      borderWidth: 1
    }]
  }), [marketShareData.products, colors]);

  // Company pie chart data
  const companyPieData = useMemo(() => ({
    labels: marketShareData.companies.map(c => c.name),
    datasets: [{
      data: marketShareData.companies.map(c => c.marketShare),
      backgroundColor: marketShareData.companies.map((c, i) => c.isPlayer ? '#3B82F6' : colors[i % colors.length]),
      borderWidth: 1
    }]
  }), [marketShareData.companies, colors]);

  const pieOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          maxWidth: 120,
          usePointStyle: true
        }
      },
      tooltip: {
        callbacks: {
          label: (context: { label: string; parsed: number }) => {
            const value = context.parsed;
            return `${context.label}: ${value.toFixed(1)}%`;
          }
        }
      }
    }
  }), []);

  // Calculate sales share data for player's products only
  const playerSalesShareData = useMemo(() => {
    const activePlayerProducts = products.filter(p => p.salesMetrics?.unitsSold && p.salesMetrics.unitsSold > 0);
    const totalPlayerUnits = activePlayerProducts.reduce((sum, p) => sum + (p.salesMetrics?.unitsSold || 0), 0);

    return {
      products: activePlayerProducts.map(p => ({
        ...p,
        salesShare: totalPlayerUnits > 0 ? ((p.salesMetrics?.unitsSold || 0) / totalPlayerUnits) * 100 : 0
      })),
      totalUnits: totalPlayerUnits
    };
  }, [products]);

  // Player sales share pie chart data
  const playerSalesPieData = useMemo(() => ({
    labels: playerSalesShareData.products.map(p => p.name),
    datasets: [{
      data: playerSalesShareData.products.map(p => p.salesShare),
      backgroundColor: colors.slice(0, playerSalesShareData.products.length),
      borderWidth: 1
    }]
  }), [playerSalesShareData.products, colors]);

  const tabs: Tab[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'pipeline', label: 'Product Pipeline' },
    { id: 'insights', label: 'Market Insights' },
    { id: 'listings', label: 'Product Listings', testId: 'listings-subtab' },
    { id: 'companies', label: 'Companies', testId: 'companies-subtab' },
    { id: 'costs', label: 'Costs' },
    { id: 'components', label: 'Components' },
  ];

  return (
    <TabContainer tabs={tabs} activeTab={activeTab} onTabChange={(tab) => setActiveTab(tab as typeof activeTab)}>
      <div className="space-y-6">
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Product Portfolio Section */}
          <div className="h-[500px]">
            <ProductPortfolioSection 
              products={products}
              onProductAction={onProductAction}
              companyName={companyName}
              gameDate={gameDate}
              getPhaseProgress={getPhaseProgress}
            />
          </div>
          
          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Player Sales Share Chart */}
            <div className="bg-theme-bg-content rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold text-theme-text-primary mb-4">Sales Share by Your Products</h3>
              <div className="h-64">
                {playerSalesShareData.products.length > 0 ? (
                  <Pie data={playerSalesPieData} options={pieOptions} />
                ) : (
                  <div className="flex items-center justify-center h-full text-theme-text-muted">
                    No products with sales data yet
                  </div>
                )}
              </div>
            </div>
            
            {/* Market Share by All Products Chart */}
            <div className="bg-theme-bg-content rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold text-theme-text-primary mb-4">Market Share by All Products</h3>
              <div className="h-64">
                {marketShareData.products.length > 0 ? (
                  <Pie data={productPieData} options={pieOptions} />
                ) : (
                  <div className="flex items-center justify-center h-full text-theme-text-muted">
                    No active products with sales data
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'pipeline' && (
        <ProductPipeline
          products={products}
          onProductAction={onProductAction}
          gameDate={gameDate}
          companyName={companyName}
          getPhaseProgress={getPhaseProgress}
          releaseModalOpen={releaseModalOpen}
          productToRelease={productToRelease}
          productReviews={productReviews}
          onReleaseModalChange={onReleaseModalChange}
          discontinueModalOpen={discontinueModalOpen}
          productToDiscontinue={productToDiscontinue}
          onDiscontinueModalChange={onDiscontinueModalChange}
          productAllocations={productAllocations}
          isProductStagePaused={isProductStagePaused}
        />
      )}

      {activeTab === 'insights' && (
        <MarketInsights 
          products={products}
          gameDate={gameDate}
        />
      )}

      {activeTab === 'listings' && (
        <div className="space-y-6">
          {/* Market Share Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-theme-bg-content rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold text-theme-text-primary mb-4">Market Share by Product</h3>
              <div className="h-64">
                {marketShareData.products.length > 0 ? (
                  <Pie data={productPieData} options={pieOptions} />
                ) : (
                  <div className="flex items-center justify-center h-full text-theme-text-muted">
                    No active products with sales data
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-theme-bg-content rounded-lg shadow p-4">
              <h3 className="text-lg font-semibold text-theme-text-primary mb-4">Market Share by Company</h3>
              <div className="h-64">
                {marketShareData.companies.length > 0 ? (
                  <Pie data={companyPieData} options={pieOptions} />
                ) : (
                  <div className="flex items-center justify-center h-full text-theme-text-muted">
                    No companies with sales data
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Product Listings Table - Pass all products with market share data */}
          <ProductListingsContainer
            products={allProducts.map(p => ({
              ...p,
              marketShare: marketShareData.products.find(mp => mp.id === p.id)?.marketShare || 0,
              inventory: devices.get(p.id)?.currentStock
            }))}
            loading={productsLoading}
            onProductAction={onProductAction}
          />
        </div>
      )}

      {activeTab === 'companies' && (
        <CompaniesTable 
          companies={companies}
          playerCompanyName={companyName}
        />
      )}

      {activeTab === 'costs' && (
        <ProductCosts 
          products={products}
          dailyStats={companyState.dailyStats}
        />
      )}

      {activeTab === 'components' && (
        <ComponentsList
          gameDate={gameDate}
          products={products}
          cash={companyState.cash}
          onCashUpdate={onCashUpdate}
          onEvent={onEvent}
        />
      )}
      </div>
    </TabContainer>
  );
});